import axios from "axios";
import { CookieUtils } from "./index";

const { REACT_APP_GTT_GQL_URL } = process.env;

// Create axios instance
const axiosInstance = axios.create({
  baseURL: REACT_APP_GTT_GQL_URL,
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    // Check for GraphQL errors in response
    if (response.data?.errors) {
      const authError = response.data.errors.find(
        (err) =>
          err.message === "AUTHORIZATION_FAILED"
         );

      if (authError) {
        console.log("[AxiosInterceptor] ⚠️ Authorization failed detected!");

        const originalRequest = response.config;

        if (isRefreshing) {
          // If already refreshing, queue this request
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers["authorization"] = token;
              return axiosInstance(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        isRefreshing = true;

        // Get tokens from cookies
        const refreshToken = CookieUtils.getCookie("gtt_refresh_token");
        const ssoToken = CookieUtils.getCookie("gtt_sso_token");
        const deviceId = CookieUtils.getCookie("gtt_device_id");

        console.log("[AxiosInterceptor] Attempting to refresh token...", {
          hasRefreshToken: !!refreshToken,
          hasSsoToken: !!ssoToken,
          deviceId,
        });

        if (!refreshToken || !deviceId) {
          isRefreshing = false;
          console.error("[AxiosInterceptor] Missing refresh_token or deviceId");
          window.location.href = "/permission-denied";
          return Promise.reject(new Error("AUTHORIZATION_FAILED"));
        }

        // Call refresh token mutation
        return axios
          .post(
            REACT_APP_GTT_GQL_URL,
            {
              query: `
            mutation AppAuthRefreshToken($deviceId: String) {
              appAuthRefreshToken(device_id: $deviceId) {
                success
                message
                code
                payload {
                  access_token
                  refresh_token
                }
              }
            }
          `,
              variables: {
                deviceId: deviceId,
              },
            },
            {
              headers: {
                "Content-Type": "application/json",
                authorization: refreshToken,
                platform: "MTI",
              },
            }
          )
          .then((res) => {
            const result = res.data;
            console.log("[AxiosInterceptor] Token Refresh Response:", result);

            if (result.data?.appAuthRefreshToken?.success) {
              const { access_token, refresh_token } =
                result.data.appAuthRefreshToken.payload;

              // Update cookies with new tokens
              CookieUtils.setCookie("gtt_access_token", access_token);
              CookieUtils.setCookie("gtt_refresh_token", refresh_token);

              console.log("[AxiosInterceptor] ✅ Token refreshed successfully");

              // Update original request with new token
              originalRequest.headers["authorization"] = access_token;

              processQueue(null, access_token);
              isRefreshing = false;

              // Retry original request
              return axiosInstance(originalRequest);
            } else {
              isRefreshing = false;
              processQueue(new Error("Token refresh failed"), null);
              console.error("[AxiosInterceptor] ❌ Token refresh failed");
              window.location.href = "/permission-denied";
              return Promise.reject(new Error("AUTHORIZATION_FAILED"));
            }
          })
          .catch((err) => {
            isRefreshing = false;
            processQueue(err, null);
            console.error(
              "[AxiosInterceptor] Error refreshing token:",
              err.message
            );
            window.location.href = "/permission-denied";
            return Promise.reject(err);
          });
      }
    }

    return response;
  },
  (error) => {
    console.error("[AxiosInterceptor] Request error:", error.message);
    return Promise.reject(error);
  }
);

export default axiosInstance;
